import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProviderApplication as ProviderForm } from '../lib/types';
import { submitProvider } from '../lib/api';

const initialForm: ProviderForm = {
  business_name: '',
  owner_name: '',
  email: '',
  phone: '',
  years_in_business: '',
  license_number: '',
  insurance_carrier: '',
  service_zips: '',
  equipment: '',
  capacity_per_month: '2',
  counties: { nueces: false, san_patricio: false, aransas: false },
  referral_interest: true,
  why_joining: '',
  consent_insurance: false,
  consent_quality: false,
  consent_accuracy: false,
  honeypot: '',
};

const inputClass =
  'w-full bg-transparent border-b-2 border-forest/20 focus:border-terracotta focus:outline-none py-3 text-forest placeholder-forest/40 transition-colors';
const labelClass = 'block text-xs tracking-[0.2em] uppercase text-forest/70 mb-2';

export function ProviderApplication() {
  const [form, setForm] = useState<ProviderForm>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();

  function update<K extends keyof ProviderForm>(field: K, val: ProviderForm[K]) {
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.business_name.trim()) e.business_name = 'Required';
    if (!form.owner_name.trim()) e.owner_name = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.insurance_carrier.trim()) e.insurance_carrier = 'Required';
    const anyCounty = form.counties.nueces || form.counties.san_patricio || form.counties.aransas;
    if (!anyCounty) e.counties = 'Select at least one county';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    setSubmitError(null);
    if (!validate()) {
      setSubmitError('Please fix the highlighted fields above.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!form.consent_insurance || !form.consent_quality || !form.consent_accuracy) {
      setSubmitError('Please confirm all three required certifications before submitting.');
      return;
    }
    setSubmitting(true);
    const result = await submitProvider(form);
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error || 'Something went wrong. Please try again.');
      return;
    }
    navigate('/thank-you/provider');
  }

  const errClass = (field: string) => (errors[field] ? 'border-terracotta' : '');

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
      <div className="text-terracotta text-sm tracking-[0.25em] uppercase mb-3">Provider Application</div>
      <h1
        className="text-5xl text-forest mb-4 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        Become a <em>Mowkin.</em>
      </h1>
      <p className="text-lg text-forest/75 max-w-2xl mb-12 leading-relaxed">
        Join the network of Coastal Bend lawn care professionals donating a small
        share of their monthly capacity. In exchange: priority lead routing, yard
        sign placement, and recognition in our community outreach.
      </p>

      {submitError && (
        <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm mb-6">
          {submitError}
        </div>
      )}

      <div className="bg-cream border border-forest/10 p-8 lg:p-12 space-y-10">
        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">01 — Business</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="sm:col-span-2">
              <label className={labelClass}>Business name *</label>
              <input
                className={`${inputClass} ${errClass('business_name')}`}
                value={form.business_name}
                onChange={(e) => update('business_name', e.target.value)}
                placeholder="Your business name, LLC"
              />
              {errors.business_name && <div className="text-terracotta text-xs mt-1">{errors.business_name}</div>}
            </div>
            <div>
              <label className={labelClass}>Owner / contact name *</label>
              <input
                className={`${inputClass} ${errClass('owner_name')}`}
                value={form.owner_name}
                onChange={(e) => update('owner_name', e.target.value)}
                placeholder="Full name"
              />
              {errors.owner_name && <div className="text-terracotta text-xs mt-1">{errors.owner_name}</div>}
            </div>
            <div>
              <label className={labelClass}>Years in business</label>
              <input
                className={inputClass}
                value={form.years_in_business}
                onChange={(e) => update('years_in_business', e.target.value)}
                placeholder="e.g., 12"
                inputMode="numeric"
              />
            </div>
            <div>
              <label className={labelClass}>Phone *</label>
              <input
                className={`${inputClass} ${errClass('phone')}`}
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="(361) 555-0123"
                inputMode="tel"
              />
              {errors.phone && <div className="text-terracotta text-xs mt-1">{errors.phone}</div>}
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input
                className={`${inputClass} ${errClass('email')}`}
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="owner@business.com"
                inputMode="email"
              />
              {errors.email && <div className="text-terracotta text-xs mt-1">{errors.email}</div>}
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">02 — Compliance</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Business license / DBA</label>
              <input
                className={inputClass}
                value={form.license_number}
                onChange={(e) => update('license_number', e.target.value)}
                placeholder="License or DBA number"
              />
            </div>
            <div>
              <label className={labelClass}>Commercial GL insurance carrier *</label>
              <input
                className={`${inputClass} ${errClass('insurance_carrier')}`}
                value={form.insurance_carrier}
                onChange={(e) => update('insurance_carrier', e.target.value)}
                placeholder="e.g., Texas Mutual, State Farm Commercial"
              />
              {errors.insurance_carrier && <div className="text-terracotta text-xs mt-1">{errors.insurance_carrier}</div>}
            </div>
          </div>
          <p className="text-xs text-forest/60 mt-3 italic">
            Providers must carry general liability coverage. A certificate of insurance will be requested upon approval.
          </p>
        </section>

        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">03 — Service Area & Capacity</h2>
          <div className="space-y-6">
            <div>
              <label className={labelClass}>Counties you serve (check all) *</label>
              <div className="grid grid-cols-3 gap-3">
                {(
                  [
                    ['nueces', 'Nueces'],
                    ['san_patricio', 'San Patricio'],
                    ['aransas', 'Aransas'],
                  ] as const
                ).map(([key, label]) => (
                  <label
                    key={key}
                    className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-colors ${
                      form.counties[key]
                        ? 'border-terracotta bg-terracotta/5'
                        : 'border-forest/15 hover:border-terracotta/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.counties[key]}
                      onChange={(e) =>
                        update('counties', { ...form.counties, [key]: e.target.checked })
                      }
                      className="accent-terracotta"
                    />
                    <span className="text-sm text-forest">{label}</span>
                  </label>
                ))}
              </div>
              {errors.counties && <div className="text-terracotta text-xs mt-2">{errors.counties}</div>}
            </div>
            <div>
              <label className={labelClass}>Specific ZIP codes (optional refinement)</label>
              <input
                className={inputClass}
                value={form.service_zips}
                onChange={(e) => update('service_zips', e.target.value)}
                placeholder="78411, 78412, 78413..."
              />
            </div>
            <div>
              <label className={labelClass}>Donated mows per month you can commit to</label>
              <div className="grid grid-cols-4 gap-3">
                {['1', '2', '4', '8'].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => update('capacity_per_month', n)}
                    className={`py-4 border-2 transition-colors ${
                      form.capacity_per_month === n
                        ? 'border-terracotta bg-terracotta text-cream'
                        : 'border-forest/20 text-forest hover:border-terracotta'
                    }`}
                  >
                    <div className="text-2xl font-display">{n}</div>
                    <div className="text-xs tracking-wider uppercase mt-1">per month</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelClass}>Equipment & crew size</label>
              <textarea
                className={inputClass + ' resize-none'}
                rows={2}
                value={form.equipment}
                onChange={(e) => update('equipment', e.target.value)}
                placeholder="e.g., 2 zero-turns, 1 push mower, string trimmers, blowers, crew of 3"
              />
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">04 — Why you're joining</h2>
          <textarea
            className={inputClass + ' resize-none'}
            rows={4}
            value={form.why_joining}
            onChange={(e) => update('why_joining', e.target.value)}
            placeholder="A few sentences about why this matters to you and your business."
          />
        </section>

        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">05 — Certifications</h2>
          <div className="space-y-4">
            {(
              [
                [
                  'consent_insurance',
                  'I carry active commercial general liability insurance and will provide a certificate upon approval.',
                ],
                [
                  'consent_quality',
                  'I commit to performing donated work at the same quality and professionalism as my paid work.',
                ],
                [
                  'consent_accuracy',
                  'The information in this application is true and accurate to the best of my knowledge.',
                ],
              ] as const
            ).map(([field, label]) => (
              <label key={field} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form[field]}
                  onChange={(e) => update(field, e.target.checked)}
                  className="mt-1 accent-terracotta"
                />
                <span className="text-sm text-forest">
                  {label} <span className="text-terracotta">*</span>
                </span>
              </label>
            ))}
          </div>
        </section>

        {/* Honeypot */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.honeypot || ''}
          onChange={(e) => update('honeypot', e.target.value)}
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          aria-hidden="true"
        />

        <div className="pt-8 border-t border-forest/10 flex justify-end">
          <button
            onClick={submit}
            disabled={submitting}
            className="bg-terracotta text-cream px-10 py-4 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting…' : 'Submit Application →'}
          </button>
        </div>
      </div>
    </div>
  );
}
